import { useState } from 'react'
import { Modal, Button, Form } from 'semantic-ui-react'
import NameField from 'components/shared/NameField'
import OrganizationField from './OrganizationField'
import CountryField from 'components/shared/CountryField'
import AccountManagerField from './AccountManagerField'
import api from 'api/index'
import Branch from 'interfaces/Branch'
import { toast } from 'react-toastify'
import { AxiosError } from 'axios'

interface FormState {
    name: string,
    organizationId: number,
    countryId: number,
    accountManagerId: number
}

const defaultState: FormState = {
    name: '',
    organizationId: -1,
    countryId: -1,
    accountManagerId: -1
}

interface PropTypes {
    onSuccess: (branch: Branch) => void
}

function CreateBranchModal(props: PropTypes) {
    const [formState, setFormState] = useState<FormState>(defaultState)
    const [isOpen, setIsOpen] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        try {
            e.preventDefault()
            setIsSubmitting(true)

            const response = await api.admin.createBranch(formState.name,
                formState.organizationId, formState.countryId, formState.accountManagerId)

            setFormState(defaultState)
            setIsOpen(false)
            props.onSuccess(response.data.branch)
            toast.success(`Successfully created branch with name '${formState.name}'.`)
        }
        catch (e) {
            if (e instanceof AxiosError) {
                const errorMessage = e.response?.data.error?.message ?? e.message
                toast.error(`Failed to create branch: ${errorMessage}.`)
            }
        }
        finally {
            setIsSubmitting(false)
        }
    }

    function isStateValid() {
        if (!formState.name ||
            formState.organizationId === defaultState.organizationId ||
            formState.countryId === defaultState.countryId ||
            formState.accountManagerId === defaultState.accountManagerId) {
            return false
        }

        return true
    }

    return (
        <Modal
            as={Form}
            open={isOpen}
            onOpen={() => setIsOpen(true)}
            onClose={() => setIsOpen(false)}
            onSubmit={handleSubmit}
            trigger={<Button basic circular size='small' icon='plus' title='Create a branch.'/>}
            size='small'
            dimmer='inverted'
        >
            <Modal.Header>Create Branch</Modal.Header>
            <Modal.Content>
                <NameField value={formState.name}
                    onChange={name => setFormState({ ...formState, name })} />
                <OrganizationField value={formState.organizationId}
                    onChange={organizationId => setFormState({ ...formState, organizationId })} />
                <CountryField value={formState.countryId}
                    onChange={countryId => setFormState({ ...formState, countryId })} />
                <AccountManagerField value={formState.accountManagerId}
                    onChange={accountManagerId => setFormState({ ...formState, accountManagerId })} />
            </Modal.Content>
            <Modal.Actions>
                <Button disabled={isSubmitting} content='Cancel'
                    type='button' onClick={() => setIsOpen(false)}/>
                <Button disabled={isSubmitting || !isStateValid()} loading={isSubmitting}
                    type='submit' content='Ok' primary />
            </Modal.Actions>
        </Modal>
    )
}

export default CreateBranchModal
